
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import commentsMetricsService from '@/services/metrics/commentsMetricsService';

interface CommentCountContextType {
  commentCount: number;
  setCommentCount: React.Dispatch<React.SetStateAction<number>>;
  refreshCommentCount: () => Promise<void>;
  isLoading: boolean;
}

const CommentCountContext = createContext<CommentCountContextType | undefined>(undefined);

export const CommentCountProvider: React.FC<{
  children: React.ReactNode;
  contentId?: string;
  contentType: 'poll' | 'card';
}> = ({ children, contentId: propContentId, contentType }) => {
  const [commentCount, setCommentCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const params = useParams<{ id?: string; pollId?: string; pollNumber?: string }>();
  
  // Use provided contentId or try to get it from route params
  const contentId = propContentId || params.id || params.pollId || params.pollNumber;
  
  const refreshCommentCount = async () => {
    if (!contentId) return;
    
    setIsLoading(true);
    try {
      const count = await commentsMetricsService.getCount(contentId, contentType);
      setCommentCount(count);
    } catch (error) {
      console.error('Error fetching comment count:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Fetch comment count on mount or when contentId changes
  useEffect(() => {
    if (contentId) {
      refreshCommentCount();
    }
  }, [contentId, contentType]);
  
  return (
    <CommentCountContext.Provider value={{
      commentCount,
      setCommentCount,
      refreshCommentCount,
      isLoading
    }}>
      {children}
    </CommentCountContext.Provider>
  );
};

export const useCommentCount = () => {
  const context = useContext(CommentCountContext);
  if (context === undefined) {
    throw new Error('useCommentCount must be used within a CommentCountProvider');
  }
  return context;
};
